import { path, pathOr, sortBy, uniqBy } from 'ramda';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import { type OpDefData } from '@frontend/api';
import {
  Flex,
  Select,
  Text,
  FeatureReleaseStatusBadge,
  type SelectProps,
} from '@frontend/components';

import { OPERATION_DEFINITION_CATEGORIES } from 'modules/opDefs';

import { fetchOpDefs } from './data/actions';
import { MANUAL_OPTION_VALUE, TK_OPTION_VALUE } from './data/constants';
import { getOpDefs, getHasInitiated, getIsLoading } from './data/reducer';

const CREATE_NEW_OPTION = 'CREATE_NEW_OPTION';

interface SelectOption {
  value: string | number;
  label: string | JSX.Element;
  data?: OpDefData;
}

type SelectionValue = SelectOption | string | OpDefData | number;

type OpDefSelectorProps = {
  allowCreateOptions?: boolean;
  allowedCategories?: string[];
  allowManualOption?: boolean;
  allowMultiSelect?: boolean;
  allowTkOption?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  defaultMenuIsOpen?: boolean;
  menuIsOpen?: boolean;
  onBlur?: (e: React.FocusEvent) => void;
  onChange: (selections: SelectionValue[]) => void;
  onFocus?: () => void;
  onInputChange?: (value: string) => void;
  selectedValues?: (string | number)[];
  menuPortalDestinationRef?: React.RefObject<HTMLElement>;
  newOperationRef?: ((instance: unknown) => void) | undefined;
} & Omit<SelectProps, 'onChange'>;

const CreateOptionsButton = styled.button`
  cursor: pointer;
  height: 30px;
  padding: 0 10px;
  text-align: left;
  width: 100%;

  :hover {
    background: #deebff;
  }
`;

interface CreateOptionNoOptionsMessageProps {
  selectOption: (option: { value: string }) => void;
  selectProps: {
    inputValue: string;
  };
}

const CreateOptionNoOptionsMessage = ({
  selectOption,
  selectProps,
}: CreateOptionNoOptionsMessageProps) => {
  return (
    <CreateOptionsButton
      onClick={() =>
        selectOption({
          value: CREATE_NEW_OPTION,
        })
      }
    >{`Use "${pathOr('', ['inputValue'], selectProps)}"`}</CreateOptionsButton>
  );
};

const RealTimeLabelWithBadge = () => (
  <Flex justifyContent="center" alignItems="center">
    <Text>+ Real-time pricing</Text>
    <FeatureReleaseStatusBadge size="xs" marginLeft={1} />
  </Flex>
);

const sortByName = sortBy((x: OpDefData) => x.name.toLowerCase());

interface SelectStyles {
  option: (
    defaultStyles: React.CSSProperties,
    { value }: { value: string },
  ) => React.CSSProperties;
}

const styles: SelectStyles = {
  option: (
    defaultStyles: React.CSSProperties,
    { value }: { value: string },
  ) => {
    if (value === MANUAL_OPTION_VALUE) {
      return {
        ...defaultStyles,
        borderBottom: '1px solid #e5e5e5',
        fontStyle: 'italic',
      };
    } else {
      return defaultStyles;
    }
  },
};

const MANUAL_OPTION: SelectOption = {
  value: MANUAL_OPTION_VALUE,
  label: '+ Manual Operation',
};

const TK_OPTION: SelectOption = {
  value: TK_OPTION_VALUE,
  label: <RealTimeLabelWithBadge />,
};

const OperationDefinitionSelector = ({
  allowCreateOptions = false,
  allowedCategories = [
    OPERATION_DEFINITION_CATEGORIES.MATERIAL,
    OPERATION_DEFINITION_CATEGORIES.OPERATION,
  ],
  allowManualOption = false,
  allowMultiSelect = false,
  allowTkOption = false,
  disabled = false,
  onBlur,
  onChange,
  onFocus,
  onInputChange,
  selectedValues = [],
  menuPortalDestinationRef,
  newOperationRef,
  ...restProps
}: OpDefSelectorProps) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');

  const opDefs = useSelector(getOpDefs);
  const hasInitiated = useSelector(getHasInitiated);
  const isLoading = useSelector(getIsLoading);

  let options: SelectOption[] = sortByName(opDefs)
    .filter(({ category }) => allowedCategories.includes(category))
    .map((opDef) => ({
      data: opDef,
      label: opDef.name,
      value: opDef.id,
    }));

  if (allowTkOption) {
    options.unshift(TK_OPTION);
  }

  if (allowManualOption) {
    options.unshift(MANUAL_OPTION);
  }

  // initiate on load if passed in values, otherwise we will initialize on focus
  useEffect(() => {
    if (!hasInitiated && !isLoading && selectedValues.length) {
      dispatch(fetchOpDefs.request());
    }
  }, [dispatch, hasInitiated, isLoading, selectedValues.length]);

  /*
  If configured to allow create options,
  create options for any strings passed in as values
  */
  if (allowCreateOptions) {
    selectedValues.forEach((value) => {
      if (typeof value === 'string') {
        options.push({
          value: value,
          label: value,
        });
      }
    });
  }

  options = uniqBy(path(['value']), options);

  const handleChange = (selectionsIn: SelectOption | SelectOption[] | null) => {
    const selections: SelectOption[] = selectionsIn
      ? Array.isArray(selectionsIn)
        ? selectionsIn
        : [selectionsIn]
      : [];

    let nextSelections: SelectionValue[] = [];
    if (
      selections.some(
        (selection: SelectOption) => selection.value === MANUAL_OPTION_VALUE,
      )
    ) {
      nextSelections = [MANUAL_OPTION];
    } else if (
      selections.some(
        (selection: SelectOption) => selection.value === TK_OPTION_VALUE,
      )
    ) {
      nextSelections = [TK_OPTION];
    } else {
      nextSelections = selections
        .map(({ value }: SelectOption) => {
          // we have an opdef and have selected an id
          if (typeof value === 'number') {
            return opDefs.find((opDef) => opDef.id === value);
          } else if (typeof value === 'string' && allowCreateOptions) {
            // we have a custom value
            if (value === CREATE_NEW_OPTION) {
              // we have a request to create new option
              return searchTerm;
            } else {
              return value;
            }
          }
        })
        .filter(
          (selection): selection is OpDefData | string =>
            selection !== undefined,
        );
    }

    onChange(nextSelections);
  };

  const handleOnBlur = (e: React.FocusEvent) => {
    // execute passed in onBlur if present
    if (onBlur) {
      onBlur(e);
    }

    // if we have create options enabled and a current input value
    // create the input value because otherwise it will get cleared
    if (allowCreateOptions && searchTerm && searchTerm.replace(/ /g, '')) {
      const values = [...(selectedValues || []), searchTerm];
      handleChange(values);
    }
  };

  const handleOnFocus = () => {
    // execute passed in onFocus prop if present
    if (onFocus) {
      onFocus();
    }

    // fetch all opdefs once on focus
    if (!hasInitiated && !isLoading) {
      dispatch(fetchOpDefs.request());
    }
  };

  const handleOnInputChange = (value: string) => {
    // execute passed in onInputChange if present
    if (onInputChange) {
      onInputChange(value);
    }
    setSearchTerm(value);
  };

  const overridenComponents: Record<
    string,
    React.ComponentType<CreateOptionNoOptionsMessageProps>
  > = {};
  if (allowCreateOptions) {
    overridenComponents['NoOptionsMessage'] = CreateOptionNoOptionsMessage;
  }

  const displaySelectValue = () =>
    selectedValues?.map((val) =>
      options.find((opt) => {
        if (opt.value === val) {
          return {
            label: opt.label,
            value: opt.value,
          };
        }
      }),
    );

  const selectValue = displaySelectValue();

  return (
    <Select
      closeMenuOnSelect={!allowMultiSelect}
      disabled={disabled}
      filterOption={(option: SelectOption, inputValue: string) =>
        (option.value !== TK_OPTION_VALUE &&
          option.label
            .toString()
            .toLowerCase()
            .indexOf(inputValue.toLowerCase()) !== -1) ||
        option.value === MANUAL_OPTION_VALUE ||
        option.value === TK_OPTION_VALUE
      }
      inputValue={searchTerm}
      isClearable={!allowMultiSelect}
      isLoading={isLoading}
      isMulti={allowMultiSelect}
      onChange={handleChange}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      onInputChange={handleOnInputChange}
      options={options}
      placeholder={
        allowMultiSelect
          ? 'Select one or more operations'
          : 'Select an operation'
      }
      styles={styles}
      value={selectValue}
      menuPortalDestinationRef={menuPortalDestinationRef}
      ref={newOperationRef}
      {...restProps}
    />
  );
};

export default OperationDefinitionSelector;
